import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AddStandardFormData } from '@/schema/template';
import {
  useInfiniteDepartmentsQuery,
  useInfiniteTouchpointsQuery,
  useInfiniteSubTouchpointsQuery,
  useInfiniteTitlesQuery,
} from '@/hooks/queries/useTemplates';

interface StandardsListProps {
  standards: AddStandardFormData[];
  onRemove: (index: number) => void;
  onSaveAll: () => void;
  propertyTypeId: string;
}

export function StandardsList({
  standards,
  onRemove,
  onSaveAll,
  propertyTypeId,
}: StandardsListProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Fetch all hierarchy data via API - fetch all items in single request
  const { data: departmentsData } = useInfiniteDepartmentsQuery(
    propertyTypeId,
    { page_size: 1000 }
  );
  const { data: touchpointsData } = useInfiniteTouchpointsQuery(
    propertyTypeId,
    { page_size: 1000 }
  );
  const { data: subTouchpointsData } = useInfiniteSubTouchpointsQuery(
    propertyTypeId,
    { page_size: 1000 }
  );
  const { data: titlesData } = useInfiniteTitlesQuery(propertyTypeId, { page_size: 1000 });

  // Flatten paginated data
  const departments = departmentsData?.pages.flatMap(page => page.data) || [];
  const touchpoints = touchpointsData?.pages.flatMap(page => page.data) || [];
  const subTouchpoints =
    subTouchpointsData?.pages.flatMap(page => page.data) || [];
  const titles = titlesData?.pages.flatMap(page => page.data) || [];

  if (standards.length === 0) {
    return null;
  }

  // Helper function to get human-readable names from API data
  const getReadableNames = (standard: AddStandardFormData) => {
    // Get department name
    const department = departments.find(
      dept => String(dept.id) === standard.department
    );
    const departmentName = department?.name || standard.department;

    // Get touchpoint name
    const touchpoint = touchpoints.find(
      tp => String(tp.id) === standard.touchpoint
    );
    const touchpointName = touchpoint?.name || standard.touchpoint;

    // Get title name
    const title = titles.find(t => String(t.id) === standard.title);
    const titleName = title?.name || standard.title;

    // Get sub-touchpoint name
    let subTouchpointName = '';
    if (standard.subtouchpoint && standard.subtouchpoint !== 'none') {
      const subTouchpoint = subTouchpoints.find(
        stp => String(stp.id) === standard.subtouchpoint
      );
      subTouchpointName = subTouchpoint?.name || standard.subtouchpoint;
    }

    return { departmentName, touchpointName, titleName, subTouchpointName };
  };

  return (
    <Card className={cn(isRTL && 'rtl')}>
      <CardHeader>
        <div
          className={cn(
            'flex items-center justify-between',
            isRTL ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          <CardTitle className={cn(isRTL && 'text-right')}>
            {t('templates.admin.addStandards.standardsList.title', {
              count: standards.length,
            })}
          </CardTitle>
          <Button onClick={onSaveAll} className="bg-primary">
            {t('templates.admin.addStandards.standardsList.saveAllStandards')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {standards.map((standard, index) => {
            const {
              departmentName,
              touchpointName,
              titleName,
              subTouchpointName,
            } = getReadableNames(standard);
            return (
              <div
                key={index}
                className={cn('border rounded-lg p-4', isRTL && 'rtl')}
              >
                <div
                  className={cn(
                    'flex items-start justify-between',
                    isRTL ? 'flex-row-reverse' : 'flex-row'
                  )}
                >
                  <div className="flex-1 space-y-2">
                    <div
                      className={cn(
                        'flex items-center gap-2 text-sm text-muted-foreground',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <Badge variant="outline">{departmentName}</Badge>
                      <Badge variant="outline">{touchpointName}</Badge>
                      {subTouchpointName && (
                        <Badge variant="outline">{subTouchpointName}</Badge>
                      )}
                      <Badge variant="outline">{titleName}</Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className={cn(isRTL && 'text-right')}>
                        <p className="text-sm font-medium">
                          {t(
                            'templates.admin.addStandards.standardsList.english'
                          )}
                        </p>
                        <p className="text-sm text-gray-700">
                          {standard.standardTextEnglish}
                        </p>
                      </div>
                      <div className={cn(isRTL && 'text-right')}>
                        <p className="text-sm font-medium">
                          {t(
                            'templates.admin.addStandards.standardsList.arabic'
                          )}
                        </p>
                        <p
                          className="text-sm text-gray-700 text-right"
                          dir="rtl"
                        >
                          {standard.standardTextArabic}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(index)}
                    className={cn(
                      'text-destructive hover:text-destructive',
                      isRTL ? 'mr-4' : 'ml-4'
                    )}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
